// Fetching the tuner's champions at boot, so a tuning run reaches players
// without a deploy.
//
// `src/engine/ai/champions.json` is committed and is therefore always as old as the
// last release. The box searches continuously and had no way to reach anybody:
// the loop was tune -> promote -> commit -> deploy, with a person in the middle
// of it. This is the same artifact taking the short way round.

import { CHAMPIONS_API_PATH } from "./tuner";
import { applyLiveChampions } from "src/engine/ai/profiles";

/**
 * Ask the server what the tuner has found, and use it if it fits this build.
 *
 * **Never throws and never blocks a game.** Every failure here — offline, a
 * 403 from the site gate, a half-written body, a tuner running a different
 * planner — has the same answer, which is that the build keeps the champions
 * it shipped with. An opponent playing slightly older weights is not a fault;
 * a game that will not start because a fetch failed is.
 *
 * Called once, before the first board. Not per game: `profileById` resolves
 * weights when a game opens and they must not change under one that is already
 * being played.
 */
export async function loadLiveChampions(signal?: AbortSignal): Promise<number> {
  try {
    const response = await fetch(CHAMPIONS_API_PATH, {
      signal,
      headers: { accept: "application/json" }
    });
    if (!response.ok) return 0;
    const payload: unknown = await response.json();
    if (!payload || typeof payload !== "object") return 0;
    // The version gate is `applyLiveChampions`' business, and deliberately not
    // repeated here — one place decides whether a vector belongs to this
    // build, or the two readings drift and the looser one wins.
    return applyLiveChampions(payload as Parameters<typeof applyLiveChampions>[0]);
  } catch {
    return 0;
  }
}
